import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [wishlistIds, setWishlistIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchSeqRef = useRef(0);
  const lastMutationAtRef = useRef(0);

  useEffect(() => {
    if (user) {
      fetchWishlist();
    } else {
      setWishlistIds([]);
    }
  }, [user]);

  const fetchWishlist = async () => {
    const seq = ++fetchSeqRef.current;
    const startedAt = Date.now();
    try {
      setLoading(true);
      const response = await axios.get(`${API}/wishlist`, { withCredentials: true });

      // Se houve uma mutação (add/remove) depois que esse fetch começou, ignora o resultado
      if (startedAt < lastMutationAtRef.current) {
        return;
      }

      // Se esse fetch não é o mais recente, ignora
      if (seq !== fetchSeqRef.current) {
        return;
      }

      setWishlistIds(response.data.product_ids || []);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      // Não zera em erro para evitar flicker / perda de estado
    } finally {
      setLoading(false);
    }
  };

  const isInWishlist = (productId) => {
    return wishlistIds.includes(productId);
  };

  const toggleWishlist = async (productId) => {
    if (!user) {
      return { success: false, error: 'not_logged_in' };
    }

    const isCurrentlyInWishlist = isInWishlist(productId);

    try {
      lastMutationAtRef.current = Date.now();
      if (isCurrentlyInWishlist) {
        await axios.delete(`${API}/wishlist/${productId}`, { withCredentials: true });
        setWishlistIds(prev => prev.filter(id => id !== productId));
        return { success: true, action: 'removed' };
      } else {
        await axios.post(`${API}/wishlist/${productId}`, {}, { withCredentials: true });
        setWishlistIds(prev => [...prev, productId]);
        return { success: true, action: 'added' };
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      return { success: false, error: 'api_error' };
    }
  };

  const value = {
    wishlistIds,
    loading,
    isInWishlist,
    toggleWishlist,
    refreshWishlist: fetchWishlist,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};
